/**
 * Spacing Tokens
 *
 * Consistent spacing scale for padding, margin, gap, and sizing.
 * Supports responsive values with mobile and desktop scales.
 *
 * @see DS-002-spacing.md
 * @figma https://www.figma.com/design/NXGWL5ErhbsA0kqoybqQbg/Design-System?node-id=220-9054
 */

import { lineHeight, lineHeightDesktop } from "../typography";

const spacingMultiplier = 2;

/**
 * Spacing scale (mobile-first)
 * Base unit: 4px
 */
export const spacing = {
  0: 0,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  "2xl": 32,
  "3xl": 40,
  "4xl": 48,
} as const;

/**
 * Desktop spacing overrides
 */
export const spacingDesktop = {
  0: spacing[0],
  xxs: spacing.xxs * spacingMultiplier, // 4px
  xs: spacing.xs * spacingMultiplier, // 8px
  sm: spacing.sm * spacingMultiplier, // 12px
  md: spacing.md * spacingMultiplier, // 16px
  lg: spacing.lg * spacingMultiplier, // 24px
  xl: spacing.xl * spacingMultiplier, // 32px
  "2xl": spacing["2xl"] * spacingMultiplier, // 40px
  "3xl": spacing["3xl"] * spacingMultiplier, // 48px
  "4xl": spacing["4xl"] * spacingMultiplier, // 56px
} as const;

/**
 * Border widths (consistent across platforms). `1-5` is Figma's
 * `spacing-size/border ✨/border-1-5`.
 */
export const borderWidth = {
  0: 0,
  1: 1,
  "1-5": 1.5,
  2: 2,
  3: 3,
  4: 4,
  8: 8,
} as const;

/**
 * Ring widths (web only: native has no ring). `focus` is Figma's
 * `spacing-size/focus ✨/focus-ring-width`; `switch-halo` is
 * `spacing-size/border ✨/switch-halo`, Switch's hover and press halo.
 */
export const ringWidth = {
  focus: 1.5,
  "switch-halo": 7,
  // Checkbox's and RadioButton's hover halo; Figma binds no width
  halo: 2,
} as const;

/**
 * Ring offsets (web only). `focus` is Figma's `spacing-size/focus ✨/focus-ring-offset`.
 */
export const ringOffsetWidth = {
  focus: 2,
} as const;

/**
 * Stroke thickness for Divider, Figma's `_outline-width/outline-1·2·3`
 */
export const outlineWidth = {
  1: 1,
  2: 2,
  3: 3,
} as const;

/**
 * Dot sizes. `badge-dot` is Figma's `spacing-size/badge ✨/badge-dot`. The
 * `presence-dot-*` keys are Avatar's indicator, keyed by Avatar's `size`, with
 * no desktop step (Figma's `_Presence badge` binds no size variable).
 */
export const dotSize = {
  "badge-dot": 8,
  "presence-dot-xs": 6,
  "presence-dot-sm": 10,
  "presence-dot-md": 10,
  "presence-dot-lg": 12,
  "presence-dot-xl": 16,
} as const;

/**
 * Control boxes. Each is a px value at every web root font size (Jason,
 * 2026-10-29), and `-dt` is the desktop step (from 768px).
 *
 * `buttonHeight` is Figma's `_button/height/*`: Button, labelled FabButton,
 * Chip and Toast's action.
 */
export const buttonHeight = { sm: 24, md: 32, lg: 40 } as const;
export const buttonHeightDesktop = { sm: 32, md: 40, lg: 48 } as const;

/** TextField's and Select's box. Figma draws no variable for it. */
export const fieldHeight = { sm: 40, md: 48 } as const;
export const fieldHeightDesktop = { sm: 48, md: 56 } as const;

/** Figma's `_tags/height/*` */
export const tagHeight = { sm: 20, md: 24, lg: 28 } as const;
export const tagHeightDesktop = { sm: 24, md: 32, lg: 40 } as const;

/**
 * Badge's pill height and minimum width: the line height of the count's type
 * (`caption-sm`, `caption-md`, `body-md`), so the pill hugs its text
 */
export const badgeHeight = {
  sm: lineHeight.caption.sm,
  md: lineHeight.caption.md,
  lg: lineHeight.body.md,
} as const;
export const badgeHeightDesktop = {
  sm: lineHeightDesktop.caption.sm,
  md: lineHeightDesktop.caption.md,
  lg: lineHeightDesktop.body.md,
} as const;

/** The minimum tap area's side, 44 × 44 */
export const touchTarget = 44;

/** Checkbox's box. Figma draws the small box 18 (desktop 22) in a 20 (24) frame. */
export const checkboxSize = { sm: 18, md: 20 } as const;
export const checkboxSizeDesktop = { sm: 22, md: 24 } as const;

/** RadioButton's outer circle */
export const radioSize = { sm: 18, md: 22 } as const;
export const radioSizeDesktop = { sm: 22, md: 26 } as const;

/** RadioButton's fill dot */
export const radioDotSize = { sm: 12, md: 14 } as const;
export const radioDotSizeDesktop = { sm: 16, md: 18 } as const;

/**
 * RadioButton's check style: its circle, which fills the control's box, so it
 * has its own sizes rather than the fill style's outer circle (Ardhi's F4
 * answer, molecules audit, 2026-11-02)
 */
export const radioCheckSize = { sm: 20, md: 24 } as const;
export const radioCheckSizeDesktop = { sm: 24, md: 28 } as const;

/**
 * OTPInputField's cell, square. The default (Medium) cell is 48, and 56 from
 * 768 (Ardhi's DQ26 answer, molecules audit, 2026-11-02); Small and Large have
 * no drawn desktop step, so they keep their size
 */
export const otpCellSize = { sm: 40, md: 48, lg: 56 } as const;
export const otpCellSizeDesktop = { sm: 40, md: 56, lg: 56 } as const;

/** Switch's track (not square, so a width and a height); no desktop step */
export const switchSize = {
  sm: { width: 32, height: 20 },
  md: { width: 40, height: 24 },
  lg: { width: 56, height: 32 },
} as const;

/** Switch's knob, and its inset from the track's outer edge, off and on (Figma) */
export const switchKnobSize = { sm: 14, md: 18, lg: 24 } as const;
export const switchKnobInset = { sm: 3, md: 3, lg: 4 } as const;

/**
 * Icon sizes (mobile-first)
 */
export const iconSize = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 28,
  "2xl": 32,
  "3xl": 40,
} as const;

/**
 * Desktop icon sizes: one step up the mobile scale (DS-002), as Figma's
 * desktop mode binds them
 */
export const iconSizeDesktop = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 28,
  xl: 32,
  "2xl": 40,
  "3xl": 48,
} as const;

/**
 * Avatar sizes (mobile-first)
 */
export const avatarSize = {
  xs: 12,
  sm: 16,
  md: 24,
  lg: 32,
  xl: 40,
  "2xl": 48,
  "3xl": 56,
} as const;

/**
 * Desktop avatar sizes: one step up the mobile scale (DS-002), as Figma's
 * desktop mode binds them
 */
export const avatarSizeDesktop = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 40,
  xl: 48,
  "2xl": 56,
  "3xl": 64,
} as const;

/**
 * CTA control sizes: Figma's `_cta/padding/*` and `_cta/line-height/*` size
 * variables (a CTA's padding, and the line its icon fills). Not the deprecated
 * `cta` text styles: labels use the `label-*` type scale.
 */
export const ctaPadding = { sm: 6, md: 8, lg: 8 } as const;
export const ctaPaddingDesktop = { sm: 8, md: 8, lg: 10 } as const;
export const ctaLineHeight = { sm: 12, md: 16, lg: 24 } as const;
export const ctaLineHeightDesktop = { sm: 16, md: 24, lg: 28 } as const;
/** The FAB's icon line: Figma's `_cta/line-height/fab-*` (FabButton's icon-only box) */
export const ctaFabLineHeight = { sm: 20, md: 24, lg: 32 } as const;
export const ctaFabLineHeightDesktop = { sm: 24, md: 28, lg: 36 } as const;

/**
 * BottomSheet's drag handle (DQ13: component tokens; Figma draws it 57×6, with
 * no variable yet). px, as the control boxes are: `w-sheet-handle`,
 * `h-sheet-handle`
 */
export const sheetHandleSize = { width: 57, height: 6 } as const;

/**
 * Tab's selected indicator: Figma's `_tab/indicator-height`, 3 at every width
 * (Ardhi's DQ15 (d) answer, molecules audit, 2026-11-03). px: `h-tab-indicator`
 */
export const tabIndicatorHeight = 3;

/**
 * Stepper's count: Figma's `_stepper/value-width/sm` and `/md`, 24 and 32 at
 * every width (Ardhi's DQ16 (c) answer, molecules audit, 2026-11-03). px:
 * `w-stepper-value-sm`, `w-stepper-value-md`
 */
export const stepperValueWidth = { sm: 24, md: 32 } as const;

/**
 * Dialog's desktop widths per `size`: `max-w-dialog-sm|md|lg`. Figma binds
 * `Modal Width ✨` to 400 and 560; 680 is the code's `lg`, which Figma doesn't
 * draw
 */
export const dialogWidth = { sm: 400, md: 560, lg: 680 } as const;

/**
 * Tooltip's maximum width, `max-w-tooltip`: Figma's tooltip symbols are drawn
 * 224 wide, with no variable
 */
export const tooltipMaxWidth = 224;

// Utility functions

/**
 * Get spacing value based on platform
 */
export const getSpacing = (key: keyof typeof spacing, isDesktop = false): number => {
  return isDesktop ? spacingDesktop[key] : spacing[key];
};

/**
 * Get icon size based on platform
 */
export const getIconSize = (key: keyof typeof iconSize, isDesktop = false): number => {
  return isDesktop ? iconSizeDesktop[key] : iconSize[key];
};

/**
 * Get avatar size based on platform
 */
export const getAvatarSize = (key: keyof typeof avatarSize, isDesktop = false): number => {
  return isDesktop ? avatarSizeDesktop[key] : avatarSize[key];
};

/**
 * A square CTA's box (IconButton): its icon line plus the padding on both
 * sides, 24/32/40 and 32/40/48 on desktop
 */
export const getCtaBoxSize = (key: CtaSizeKey, isDesktop = false): number => {
  return isDesktop
    ? ctaLineHeightDesktop[key] + 2 * ctaPaddingDesktop[key]
    : ctaLineHeight[key] + 2 * ctaPadding[key];
};

/**
 * An icon-only FAB's box: its fab icon line plus the CTA padding on both
 * sides, 32/40/48 and 40/44/56 on desktop
 */
export const getCtaFabBoxSize = (key: CtaSizeKey, isDesktop = false): number => {
  return isDesktop
    ? ctaFabLineHeightDesktop[key] + 2 * ctaPaddingDesktop[key]
    : ctaFabLineHeight[key] + 2 * ctaPadding[key];
};

/**
 * How far Switch's knob moves from the track's content edge, inside its 2px
 * border (`borderWidth[2]`): the inset minus the border off, and the track
 * less the inset, the knob and the border on. 1/1/2 off, 13/17/26 on.
 */
export const getSwitchKnobOffset = (key: SwitchSizeKey, checked: boolean): number => {
  const border = borderWidth[2];
  return checked
    ? switchSize[key].width - switchKnobInset[key] - switchKnobSize[key] - border
    : switchKnobInset[key] - border;
};

// Type exports
export type SpacingKey = keyof typeof spacing;
export type BorderWidthKey = keyof typeof borderWidth;
export type RingWidthKey = keyof typeof ringWidth;
export type RingOffsetWidthKey = keyof typeof ringOffsetWidth;
export type OutlineWidthKey = keyof typeof outlineWidth;
export type DotSizeKey = keyof typeof dotSize;
export type IconSizeKey = keyof typeof iconSize;
export type AvatarSizeKey = keyof typeof avatarSize;
export type CtaSizeKey = keyof typeof ctaPadding;
export type ButtonHeightKey = keyof typeof buttonHeight;
export type FieldHeightKey = keyof typeof fieldHeight;
export type TagHeightKey = keyof typeof tagHeight;
export type BadgeHeightKey = keyof typeof badgeHeight;
export type CheckboxSizeKey = keyof typeof checkboxSize;
export type RadioSizeKey = keyof typeof radioSize;
export type RadioDotSizeKey = keyof typeof radioDotSize;
export type RadioCheckSizeKey = keyof typeof radioCheckSize;
export type OtpCellSizeKey = keyof typeof otpCellSize;
export type SwitchSizeKey = keyof typeof switchSize;
export type DialogWidthKey = keyof typeof dialogWidth;
